import React, { ChangeEvent, Suspense, forwardRef, lazy, useImperativeHandle, useRef, useState } from 'react';
import ProcessingIndicator from './ProcessingIndicator';
import 'cropperjs/dist/cropper.css';
import { Modal } from 'react-bootstrap';
import Button from "./Button";

// cropperjs is only needed once the crop modal opens — lazy-load the component
// so the library ships in its own chunk instead of every page using this upload.
const Cropper = lazy(() => import('react-cropper'));

interface FileUploadProps {
    onFileSelect: (file: File  | null) => void;
    onRemoveFile?: () => void;
    canEdit:boolean;
    /** There is a current photo — the button says "Change photo" instead of "Add photo". */
    hasPhoto?: boolean;
}

/** Lets a photo tile hand over a file dropped on it (same path as picking one). */
export interface FileUploadHandle {
    takeFile: (file: File) => void;
}

/**
 * Photo / logo picker: a labelled "Add photo" / "Change photo" button (never
 * the native file box), Remove, and the crop / background-removal dialog that
 * opens once a file is chosen.
 */
const FileUpload = forwardRef<FileUploadHandle, FileUploadProps>(({ onFileSelect, onRemoveFile, canEdit, hasPhoto = true }, ref) => {
    const [initialFile, setInitialFile] = useState<File | null>(null);
    const [currentFile, setCurrentFile] = useState<File | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [cropper, setCropper] = useState<any>(null);
    const [showModal, setShowModal] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const takeFile = (file: File) => {
        setInitialFile(file)
        setCurrentFile(file);
        onFileSelect(file);
        handleOpenModal()
    };
    useImperativeHandle(ref, () => ({ takeFile }));

    const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            takeFile(file);
        }
        // Picking the same file again must still fire a change.
        e.target.value = "";
    };
    const handleRemoveClick = () => {
        if (onRemoveFile) {
            onRemoveFile();
        }
        setCurrentFile(null);
        onFileSelect(null); // Notify parent that file has been cleared
    };
    const handleBackgroundRemoveClick = async () => {
        if (currentFile) {
            try {
                setLoading(true);
                // @imgly/background-removal (plus its wasm model) is far too
                // heavy to bundle — load it only when the button is clicked.
                const { removeBackground } = await import('@imgly/background-removal');
                const processedBlob = await removeBackground(currentFile);
                const processedFile = new File([processedBlob], currentFile.name, { type: processedBlob.type, lastModified: Date.now() });
                setCurrentFile(processedFile); // Store the processed file
                onFileSelect(processedFile); // Notify parent with the processed file
            } catch (error) {
                console.error("Background removal failed:", error);
            }
            finally {
                setLoading(false);
            }
        }
    };
    const handleCrop = () => {
        if (cropper) {
            const croppedCanvas = cropper.getCroppedCanvas(); // Get the cropped image canvas
            croppedCanvas.toBlob((blob:any) => {
                if (blob) {
                    const croppedFile = new File([blob], currentFile?.name || 'cropped-image', { type: blob.type });
                    setCurrentFile(croppedFile); // Store the cropped file
                    onFileSelect(croppedFile); // Notify parent with the cropped file
                }
            });
        }
    };
    const handleCloseModal = () => {
        setShowModal(false); // Close the cropper modal
    };

    const handleOpenModal = () => {
        setShowModal(true); // Open the cropper modal
    };
    const handleResetClick = () => {
        if (currentFile) {
            setCurrentFile(initialFile);
            onFileSelect(initialFile);
        }
    };
    return (
        <>
        { canEdit && (
        <div className="image-upload-controls">
            <input ref={inputRef} type="file" accept="image/*" aria-label="Choose an image to upload" onChange={handleFileChange} disabled={loading || !canEdit} hidden />
            <Button type="button" size="sm" icon="upload" title="Upload a photo or logo — you can crop it next" disabled={loading} onClick={() => inputRef.current?.click()}>
                {hasPhoto ? "Change photo" : "Add photo"}
            </Button>
            {onRemoveFile && !loading && hasPhoto && (
                <Button type="button" size="sm" variant="ghost" icon="trash" title="Remove the current photo" onClick={handleRemoveClick}>
                    Remove
                </Button>
            )}

        {/* Cropper Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Crop photo</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {currentFile && !loading && (
                        <Suspense fallback={<ProcessingIndicator />}>
                            <Cropper
                                src={URL.createObjectURL(currentFile)} // Convert File to URL
                                style={{ height: 400, width: '100%' }}
                                aspectRatio={1}
                                guides={true}
                                initialAspectRatio={1}
                                viewMode={1}
                                autoCropArea={0.8}
                                responsive={true}
                                checkOrientation={false}
                                onInitialized={(instance) => setCropper(instance)}
                            />
                        </Suspense>
                    )}
                    {loading && (
                        <ProcessingIndicator />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button type="button" variant="ghost" icon="person-square" title="Remove the background (runs in your browser)" onClick={handleBackgroundRemoveClick}>Remove background</Button>
                    <Button type="button" variant="ghost" icon="arrow-counterclockwise" title="Back to the original image" onClick={handleResetClick}>Reset</Button>
                    <Button type="button" icon="crop" title="Keep the selected square" onClick={handleCrop}>Crop</Button>
                    <Button type="button" variant="primary" icon="check2" title="Use this image" onClick={handleCloseModal}>Done</Button>
                </Modal.Footer>
            </Modal>
        </div>
    )}
    </>
    );
});

FileUpload.displayName = "FileUpload";

export default FileUpload;
